import { tryOnScopeDispose, useEventListener } from '@vueuse/core'
import { clamp, isNull } from 'lodash'
import { toValue } from 'vue'

// MouseEvent.button value of the main (usually left) mouse button
const MAIN_MOUSE_BUTTON = 0

// Makes sortable drag'n'drop (vue-virtual-draglist) depend only on the cursor Y.
// The library resolves the drop target from the mousemove target and requires
// the cursor X to be inside the list rect, so we swallow real mousemoves and
// re-dispatch them at a fixed X inside the dragged item.
// areaReference (optional): the drag reacts only while the cursor is inside this
// element, moves outside of it are ignored. Without it the whole page is active.
// To disable the lock, don't call the composable (e.g. wrap the call in a condition).
export const useVerticalDragLock = ({
  containerReference,
  areaReference = null,
  handleSelector = '.drag-trigger',
  itemSelector
} = {}) => {
  if (!itemSelector) {
    throw new Error('useVerticalDragLock: itemSelector is required')
  }

  let lockedClientX = null
  let isDispatchingLockedMove = false
  let stopLockListeners = []

  // clientX and clientY are taken from the real mousemove we are about to re-send:
  // clientY is re-sent as is, clientX is replaced with lockedClientX
  const isInsideArea = ({ clientX, clientY }) => {
    const area = toValue(areaReference)
    if (!area) return true

    const { left, right, top, bottom } = area.getBoundingClientRect()
    return clientX >= left && clientX <= right && clientY >= top && clientY <= bottom
  }

  const onLockedMouseMove = event => {
    if (isDispatchingLockedMove || isNull(lockedClientX)) return

    event.stopImmediatePropagation()

    if (!isInsideArea(event)) return

    const target =
      document.elementFromPoint(lockedClientX, event.clientY) || document.documentElement

    isDispatchingLockedMove = true
    try {
      target.dispatchEvent(
        new MouseEvent('mousemove', {
          bubbles: true,
          cancelable: true,
          clientX: lockedClientX,
          clientY: event.clientY,
          screenX: event.screenX,
          screenY: event.screenY,
          buttons: event.buttons
        })
      )
    } finally {
      isDispatchingLockedMove = false
    }
  }

  const releaseLock = () => {
    lockedClientX = null
    stopLockListeners.forEach(stop => stop())
    stopLockListeners = []
  }

  const onMouseDown = event => {
    if (event.button !== MAIN_MOUSE_BUTTON) return

    // a missed mouseup would leave the previous lock listeners behind
    releaseLock()

    const container = toValue(containerReference)
    const handle = event.target.closest?.(handleSelector)
    if (!container || !handle || !container.contains(handle)) return

    const item = handle.closest(itemSelector)
    if (!item) {
      console.error(
        `useVerticalDragLock: no "${itemSelector}" element found around the drag handle`
      )
      return
    }

    // the handle may sit outside the item (e.g. negative margin on the homepage),
    // so clamp X into the item rect
    const { left, right } = item.getBoundingClientRect()
    lockedClientX = clamp(event.clientX, left + 1, right - 1)

    // the drag helper is offset by (clientX - startX), so start from the same X
    // to keep it moving strictly vertically.
    // clientX is a getter on MouseEvent.prototype, so an own property shadows it for
    // every listener handling this same event later. We listen in the capture phase
    // on document, so the library's mousedown listener runs after us and reads it.
    Object.defineProperty(event, 'clientX', { value: lockedClientX })

    stopLockListeners = [
      useEventListener(window, 'mousemove', onLockedMouseMove, { capture: true }),
      useEventListener(window, 'mouseup', releaseLock, { capture: true }),
      // the library relies on preventing mousemove default to stop text selection,
      // but it only receives our re-sent events, so prevent the selection itself
      useEventListener(document, 'selectstart', event => event.preventDefault(), {
        capture: true
      })
    ]
  }

  useEventListener(document, 'mousedown', onMouseDown, { capture: true })

  // the mousedown listener above is removed on scope dispose by useEventListener itself,
  // but the lock listeners are added from onMouseDown, outside of any scope, so they
  // aren't. Release them manually in case of unmount in the middle of a drag
  tryOnScopeDispose(releaseLock)
}
